import { ImgID } from "../../image_loader/img_id.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { AbstractTooltip } from "./abstract_tooltip.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";

interface IContentItem
{
  img_id: ImgID;
  count: number;
  amount_text?: string;
}

export class AdventureTooltip extends AbstractTooltip
{
  private background_element!: UINineSliceElement;
  private tail_element!: UIImage;
  private tail_x: number;

  private readonly TAIL_WIDTH: number = LayoutScaler.widthFromDesign( 111 );
  private readonly TAIL_HEIGHT: number = LayoutScaler.heightFromDesign( 78 );
  private readonly TAIL_Y_OFFSET: number = LayoutScaler.heightFromDesign( -32 );

  private static readonly GRID_COLS = 3;
  private static readonly ITEM_W = 114;
  private static readonly ITEM_H = 114;
  private static readonly ITEM_GAP_X = 50;
  private static readonly ITEM_GAP_Y = 20;
  private static readonly CONTENT_TOP_Y = 35;
  private static readonly CONTENT_BOTTOM_PAD = 51;
  private static readonly SIDE_PAD = 68;

  static computeSize( item_count: number ): { width: number; height: number }
  {
    const cols = Math.min( this.GRID_COLS, Math.max( 1, item_count ) );
    const rows = Math.ceil( Math.max( 1, item_count ) / cols );

    const width_design = cols * this.ITEM_W + ( cols - 1 ) * this.ITEM_GAP_X + this.SIDE_PAD * 2;
    const height_design = this.CONTENT_TOP_Y + rows * this.ITEM_H + ( rows - 1 ) * this.ITEM_GAP_Y + this.CONTENT_BOTTOM_PAD;

    return {
      width: LayoutScaler.widthFromDesign( width_design ),
      height: LayoutScaler.heightFromDesign( height_design ),
    };
  }

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    tail_x: number,
    content: IContentItem[]
  )
  {
    super( ctx, x, y, width, height );

    this.tail_x = tail_x;

    this.createBackgroundElement();
    this.createTailElement();
    this.createContent( content );
  }

  private createContent( content: IContentItem[] ): void
  {
    const item_w = LayoutScaler.widthFromDesign( AdventureTooltip.ITEM_W );
    const item_h = LayoutScaler.heightFromDesign( AdventureTooltip.ITEM_H );
    const item_gap_x = LayoutScaler.widthFromDesign( AdventureTooltip.ITEM_GAP_X );
    const item_gap_y = LayoutScaler.heightFromDesign( AdventureTooltip.ITEM_GAP_Y );
    const cols = Math.min( AdventureTooltip.GRID_COLS, Math.max( 1, content.length ) );
    const top_y = LayoutScaler.yFromDesign( AdventureTooltip.CONTENT_TOP_Y );

    content.forEach( ( item, i ) =>
    {
      const row = Math.floor( i / cols );
      const col = i % cols;
      const items_in_row = Math.min( cols, content.length - row * cols );
      const row_w = items_in_row * item_w + ( items_in_row - 1 ) * item_gap_x;
      const row_start_x = ( this.rect.getW() - row_w ) / 2;

      const item_x = row_start_x + col * ( item_w + item_gap_x );
      const y = top_y + row * ( item_h + item_gap_y );

      const item_img = new UIImage(
        this.ctx,
        item_x,
        y,
        item_w,
        item_h,
        item.img_id
      );

      this.addChild( item_img, true );

      const text_h = LayoutScaler.heightFromDesign( 52 );
      const text_y = item_h - text_h + LayoutScaler.yFromDesign( 15 );

      const text = new UITextElement(
        this.ctx,
        0,
        text_y + LayoutScaler.yFromDesign( 2 ),
        item_w,
        text_h,
        {
          text: item.amount_text ?? `x${item.count}`,
          font_size: LayoutScaler.textSizeFromDesign( 38 ),
          text_align: MyTextAlign.Center,
          text_style: TextStyleScheme.geologica,
          text_color: ColorScheme.white,
          font_weight: 900,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
          outline_color: ColorScheme.booster_dialog_quantity_outline,
        }
      );

      const text_1 = new UITextElement(
        this.ctx,
        0,
        text_y,
        item_w,
        text_h,
        {
          text: item.amount_text ?? `x${item.count}`,
          font_size: LayoutScaler.textSizeFromDesign( 38 ),
          text_align: MyTextAlign.Center,
          text_style: TextStyleScheme.geologica,
          text_color: ColorScheme.white,
          font_weight: 900,
        }
      );
      
      item_img.addChild( text, true );
      item_img.addChild( text_1, true );
    } );
  }

  private createBackgroundElement(): void
  {
    this.background_element = new UINineSliceElement(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      ImgID.UI_TutorialSubstrate,
      "tutorial_substrate"
    )

    this.addChild( this.background_element, true );
  }

  private createTailElement(): void
  {
    const tail_x = this.tail_x - this.TAIL_WIDTH / 2;
    const tail_y = this.rect.getH() + this.TAIL_Y_OFFSET;

    this.tail_element = new UIImage(
      this.ctx,
      tail_x,
      tail_y,
      this.TAIL_WIDTH,
      this.TAIL_HEIGHT,
      ImgID.UI_TooltipArrow
    );

    this.addChild( this.tail_element, true );
  }

  public setTailX( design_tail_x: number ): void
  {
    this.tail_x = LayoutScaler.widthFromDesign( design_tail_x );
    this.updateTailPosition();
    this.setShouldBeRendered();
  }

  private updateTailPosition(): void
  {
    if ( this.tail_element )
    {
      this.removeChild( this.tail_element );
    }

    this.createTailElement();
  }

  name(): string
  {
    return 'AdventureTooltip';
  }

  render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
